import { bisect } from 'd3-array';

import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfHour,
  endOfISOWeek,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  isValid,
  parseISO,
  startOfDay,
  startOfHour,
  startOfISOWeek,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  StartOfWeekOptions,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from 'date-fns';
import { format, formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

import { isObject, omitBy } from 'lodash';

import {
  isCompleteShorthand,
  isDynamicShorthand,
  TimePrecision,
  TimeRange,
  timePrecision,
} from './types';
import { TimeAggregationPeriod } from '../explore/types';

export const DateFormat = 'yyyy-MM-dd';
export const DateTimeFormat = "yyyy-MM-dd'T'HH:mm";
export const DateFriendlyFormat = 'LLL d yyyy';
export const DateFriendlyTimeFormat = 'LLL d yyyy, HH:mm';

// Format "yyyy-MM-dd" or "yyyy-MM-dd'T'HH:mm"
export interface DateRange {
  from: string;
  to: string;
}

interface Interval {
  years?: number;
  months?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
}

export const isDateRange = (value: unknown): value is DateRange =>
  typeof value === 'object' && value !== null && 'from' in value && 'to' in value;

export const getHumanReadableRange = (value: Partial<DateRange>, timezone: string) => {
  const from = value.from ?? '-';
  const fromIncludesTime = includesTime(from);

  const to = value.to ?? '-';
  const toIncludesTime = includesTime(to);

  // Check if both dates are on the same calendar day
  const fromDay = from !== '-' ? formatInTimeZone(from, timezone, 'yyyy-MM-dd') : null;
  const toDay = to !== '-' ? formatInTimeZone(to, timezone, 'yyyy-MM-dd') : null;
  const sameDay = fromDay !== null && toDay !== null && fromDay === toDay;

  // For same-day ranges, return just the single date (unless user explicitly set different times)
  if (sameDay) {
    // If neither input includes time (date-only strings), show as single date
    if (!fromIncludesTime && !toIncludesTime) {
      return formatInTimeZone(to, timezone, DateFriendlyFormat);
    }

    // Times at start/end of day (00:00, 23:59) are considered "no meaningful time"
    const fromTime = from !== '-' ? formatInTimeZone(from, timezone, 'HH:mm') : null;
    const toTime = to !== '-' ? formatInTimeZone(to, timezone, 'HH:mm') : null;
    const fromIsStartOfDay = fromTime === '00:00';
    const toIsEndOfDay = toTime === '23:59';

    // If times are just start/end of day, show as single date without time
    if (fromIsStartOfDay && toIsEndOfDay) {
      return formatInTimeZone(to, timezone, DateFriendlyFormat);
    }

    // If both times are the same, show single date with time
    if (fromTime === toTime) {
      return formatInTimeZone(to, timezone, DateFriendlyTimeFormat);
    }
  }

  const toFormat = toIncludesTime ? DateFriendlyTimeFormat : DateFriendlyFormat;
  const toFormatted = formatInTimeZone(to, timezone, toFormat);

  // Check if both dates are in the same year - if so, omit year from first date
  const fromYear = from !== '-' ? formatInTimeZone(from, timezone, 'yyyy') : null;
  const toYear = to !== '-' ? formatInTimeZone(to, timezone, 'yyyy') : null;
  const sameYear = fromYear !== null && toYear !== null && fromYear === toYear;

  const fromFormatNoYear = fromIncludesTime ? 'LLL d HH:mm' : 'LLL d';
  const fromFormat = sameYear
    ? fromFormatNoYear
    : fromIncludesTime
      ? DateFriendlyTimeFormat
      : DateFriendlyFormat;
  const fromFormatted = formatInTimeZone(from, timezone, fromFormat);

  if (fromFormatted === toFormatted) {
    return toFormatted;
  }

  return `${fromFormatted} – ${toFormatted}`;
};

export const getISOFormatForPrecision = (
  precision: Exclude<TimeAggregationPeriod, 'day_of_week'>,
) => (precision === 'hour' ? DateTimeFormat : DateFormat);

const getLocalDayNames = (
  locale = 'en',
  format: 'long' | 'short' | 'narrow' | undefined = 'long',
) => {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: format, timeZone: 'UTC' });
  const days = [2, 3, 4, 5, 6, 7, 1].map((day) => {
    const dd = day < 10 ? `0${day}` : day;
    return new Date(`2017-01-${dd}T00:00:00+00:00`);
  });
  return days.map((date) => formatter.format(date));
};

const localDayNames = getLocalDayNames();

export const getWeekdayName = (dow: number) => localDayNames[dow - 1];

export const combineDateTime = (date: string, time?: string) =>
  `${date}${time !== '' && time !== undefined ? `T${time}` : ''}`;

/**
 * Format a date for display in a given timezone.
 * @param date The Date, ISO string or timestamp to format.
 */
export const formatDate = (
  date: string | number | Date,
  fmt: string,
  timezone?: string,
): string => {
  return format(timezone !== undefined ? toZonedTime(date, timezone) : new Date(date), fmt, {
    timeZone: timezone,
  });
};

export const formatLocalDateTime = (date: string | number | Date): string =>
  formatDate(date, 'MMMM do yyyy HH:mm:ss z');

const IntervalFormats: { [key in TimeAggregationPeriod]: string } = {
  year: 'yyyy',
  quarter: 'QQQ yyyy',
  month: 'MMMM yyyy',
  week: "'W'I RRRR",
  day_of_week: 'EEEE',
  day: 'MMM do yyyy',
  hour: 'MMM do yyyy HH:mm',
};

export const getIntervalFormat = (timeInterval?: TimeAggregationPeriod) => {
  return timeInterval !== undefined ? IntervalFormats[timeInterval] : 'MMMM yyyy HH:mm:ss OOOO';
};

export function getFormattedDateForPeriod(
  date: Date,
  aggPeriod: TimeAggregationPeriod | null,
  timezone?: string,
) {
  date = timezone !== undefined ? toZonedTime(date, timezone) : date;
  if (aggPeriod === null) {
    return formatLocalDateTime(date);
  }

  if (aggPeriod === 'week') {
    const startOf = startOfISOWeek(date);
    const endOf = endOfISOWeek(date);
    if (
      startOf.getFullYear() !== new Date().getFullYear() ||
      endOf.getFullYear() !== new Date().getFullYear()
    ) {
      // prettier-ignore
      return `${formatDate(startOf, 'MMM do yyyy')}–${formatDate(endOf, 'MMM do yyyy')}`;
    }
    return `${formatDate(startOf, 'MMM do')}–${formatDate(endOf, 'MMM do')}`;
  }
  return formatDate(date, getIntervalFormat(aggPeriod));
}

export const dateSort = (a: Date, b: Date) => a.getTime() - b.getTime();

export const findClosestDate = (values: Date[], target: Date): Date | undefined =>
  values[bisect(values, target)];

// Parse dynamic shorthand pattern like "21d", "3w", "6m", "2q", "2y" into count and unit
const parseDynamicShorthand = (pattern: string): { count: number; unit: string } | null => {
  const match = pattern.match(/^(\d+)([dwmqy])$/);
  if (match === null) {
    return null;
  }
  return { count: parseInt(match[1], 10), unit: match[2] };
};

// Calculate start date from dynamic shorthand pattern
const getFromDateForDynamicShorthand = (pattern: string): Date => {
  const parsed = parseDynamicShorthand(pattern);
  if (parsed === null) {
    throw new Error(`Invalid dynamic shorthand pattern: ${pattern}`);
  }

  const { count, unit } = parsed;
  const now = new Date();

  switch (unit) {
    case 'd':
      return startOfDay(subDays(now, count));
    case 'w':
      return startOfDay(subWeeks(now, count));
    case 'm':
      return startOfDay(subMonths(now, count));
    case 'q':
      return startOfDay(subQuarters(now, count));
    case 'y':
      return startOfDay(subYears(now, count));
    default:
      throw new Error(`Unknown unit: ${unit}`);
  }
};

// Parse "complete" shorthand pattern like "c2w", "c6m" into count and unit
const parseCompleteShorthand = (pattern: string): { count: number; unit: string } | null => {
  const match = pattern.match(/^c(\d+)([dwmqy])$/);
  if (match === null) {
    return null;
  }
  return { count: parseInt(match[1], 10), unit: match[2] };
};

// Calculate start date for "complete" period shorthand (e.g., "c2w" = last 2 complete weeks)
// "Complete" means we exclude the current incomplete period
const getFromDateForCompleteShorthand = (pattern: string): Date => {
  const parsed = parseCompleteShorthand(pattern);
  if (parsed === null) {
    throw new Error(`Invalid complete shorthand pattern: ${pattern}`);
  }

  const { count, unit } = parsed;
  const now = new Date();

  switch (unit) {
    case 'd': {
      // "c2d" = yesterday and day before (exclude today)
      const yesterday = subDays(now, 1);
      return startOfDay(subDays(yesterday, count - 1));
    }
    case 'w': {
      // "c2w" = last 2 complete weeks (exclude current week)
      const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
      const prevWeekStart = subWeeks(currentWeekStart, 1);
      return startOfWeek(subWeeks(prevWeekStart, count - 1), { weekStartsOn: 1 });
    }
    case 'm': {
      // "c6m" = last 6 complete months (exclude current month)
      const currentMonthStart = startOfMonth(now);
      const prevMonthStart = subMonths(currentMonthStart, 1);
      return startOfMonth(subMonths(prevMonthStart, count - 1));
    }
    case 'q': {
      // "c2q" = last 2 complete quarters (exclude current quarter)
      const currentQuarterStart = startOfQuarter(now);
      const prevQuarterStart = subQuarters(currentQuarterStart, 1);
      return startOfQuarter(subQuarters(prevQuarterStart, count - 1));
    }
    case 'y': {
      // "c2y" = last 2 complete years (exclude current year)
      const currentYearStart = startOfYear(now);
      const prevYearStart = subYears(currentYearStart, 1);
      return startOfYear(subYears(prevYearStart, count - 1));
    }
    default:
      throw new Error(`Unknown unit: ${unit}`);
  }
};

// Calculate end date for "complete" period shorthand (e.g., "c2w" = end of last complete week)
const getToDateForCompleteShorthand = (pattern: string): Date => {
  const parsed = parseCompleteShorthand(pattern);
  if (parsed === null) {
    throw new Error(`Invalid complete shorthand pattern: ${pattern}`);
  }

  const { unit } = parsed;
  const now = new Date();

  switch (unit) {
    case 'd': {
      // End of yesterday
      return endOfDay(subDays(now, 1));
    }
    case 'w': {
      // End of last complete week
      const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
      const prevWeekEnd = subDays(currentWeekStart, 1);
      return endOfDay(prevWeekEnd);
    }
    case 'm': {
      // End of last complete month
      const currentMonthStart = startOfMonth(now);
      const prevMonthEnd = subDays(currentMonthStart, 1);
      return endOfDay(prevMonthEnd);
    }
    case 'q': {
      // End of last complete quarter
      return endOfQuarter(subQuarters(now, 1));
    }
    case 'y': {
      // End of last complete year
      return endOfYear(subYears(now, 1));
    }
    default:
      throw new Error(`Unknown unit: ${unit}`);
  }
};

export const DateRangeOptions: { label: string; value: TimeRange }[] = [
  { label: 'Custom', value: 'custom' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
  { label: 'Last 1 year', value: '1y' },
  { label: 'Year to date', value: 'ytd' },
  { label: 'Month to date', value: 'mtd' },
  { label: 'Last week', value: 'prevweek' },
  { label: `Last year (${subYears(new Date(), 1).getFullYear()})`, value: 'prevyear' },
  { label: `Last month (${format(subMonths(new Date(), 1), 'MMMM')})`, value: 'prevmonth' },
];

export function getFromDate(timeRangePreset: TimeRange, aggPeriod: TimePrecision) {
  // Handle "complete" shorthand patterns like "c2w", "c6m" (last N complete periods)
  if (isCompleteShorthand(timeRangePreset)) {
    return getFromDateForCompleteShorthand(timeRangePreset);
  }

  // Handle dynamic shorthand patterns like "21d", "3w", "6m", "2y"
  if (isDynamicShorthand(timeRangePreset)) {
    return getFromDateForDynamicShorthand(timeRangePreset);
  }

  switch (timeRangePreset) {
    case '1y': {
      switch (aggPeriod) {
        case timePrecision.enum.Daily:
          return startOfDay(subYears(new Date(), 1));
        case timePrecision.enum.Weekly:
          return startOfWeek(subYears(new Date(), 1));
        case timePrecision.enum.Monthly:
        default:
          return startOfMonth(subYears(new Date(), 1));
      }
    }
    case 'today':
      return startOfDay(new Date());
    case 'tomorrow':
      return startOfDay(addDays(new Date(), 1));
    case 'yesterday':
      return startOfDay(subDays(new Date(), 1));
    case '7d':
      return startOfDay(subDays(new Date(), 7));
    case '30d':
      return startOfDay(subDays(new Date(), 30));
    case '90d':
    case 'custom':
      return startOfDay(subDays(new Date(), 90));
    case 'wtd':
      return startOfWeek(new Date(), { weekStartsOn: 1 });
    case 'ytd':
      return startOfYear(new Date());
    case 'mtd':
      return startOfMonth(new Date());
    case 'qtd':
      return startOfQuarter(new Date());
    case 'prevquarter':
      return startOfQuarter(subQuarters(new Date(), 1));
    case 'nextweek':
      return startOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 });
    case 'prevweek':
      return startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 });
    case 'nextmonth':
      return startOfMonth(addMonths(new Date(), 1));
    case 'prevmonth':
      return startOfMonth(subMonths(new Date(), 1));
    case 'prevyear':
      return startOfYear(subYears(new Date(), 1));
    default:
      throw new Error(`Unhandled timeRangePreset: ${timeRangePreset}`);
  }
}

export function getToDate(timeRangePreset: TimeRange) {
  // Handle "complete" shorthand patterns - end at the last complete period boundary
  if (isCompleteShorthand(timeRangePreset)) {
    return getToDateForCompleteShorthand(timeRangePreset);
  }

  // Dynamic shorthand patterns always end at "now"
  if (isDynamicShorthand(timeRangePreset)) {
    return new Date();
  }

  switch (timeRangePreset) {
    case 'today':
      return endOfDay(new Date());
    case 'tomorrow':
      return endOfDay(addDays(new Date(), 1));
    case 'yesterday':
      return endOfDay(subDays(new Date(), 1));
    case '1y':
    case '7d':
    case '30d':
    case '90d':
    case 'wtd':
    case 'ytd':
    case 'mtd':
    case 'qtd':
    case 'custom':
      return new Date();
    case 'nextweek':
      return endOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 });
    case 'prevweek':
      return endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 });
    case 'prevquarter':
      return endOfQuarter(subQuarters(new Date(), 1));
    case 'nextmonth':
      return endOfMonth(addMonths(new Date(), 1));
    case 'prevmonth':
      return endOfMonth(subMonths(new Date(), 1));
    case 'prevyear':
      return endOfYear(subYears(new Date(), 1));
    default:
      throw new Error(`Unhandled timeRangePreset: ${timeRangePreset}`);
  }
}

export const getDatesFromTimeRange = (timeRangePreset: TimeRange, aggPeriod: TimePrecision) => {
  return {
    startDate: getFromDate(timeRangePreset, aggPeriod),
    endDate: getToDate(timeRangePreset),
  };
};

const isoDateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
const isoDateRegex = /^\d{4}-\d{2}-\d{2}/;

const isISODateTimeString = (value: string) => isoDateTimeRegex.test(value);

const isISODateString = (value: string) => isoDateRegex.test(value);

export const containsISODateString = (value: string) =>
  isISODateString(value) || isISODateTimeString(value);

export const includesTime = (value: string): boolean => value.substring(11) !== '';

export const getDefaultFormattedLocalDate = () => format(new Date(), DateFormat);

const DateTimeWithSecondsFormat = "yyyy-MM-dd'T'HH:mm:ss";
const isoOffsetRegex = /(?:[Zz]|[+-]\d{2}:?\d{2})$/;
const includesSeconds = (value: string): boolean => /T\d{2}:\d{2}:\d{2}/.test(value);

export const ensureLocalDateFormat = (value: string, timezone?: string): string => {
  const parsedValue = parseISO(value);
  if (!isValid(parsedValue)) {
    return getDefaultFormattedLocalDate();
  }
  if (!includesTime(value)) {
    return format(parsedValue, DateFormat);
  }
  const dateFormat = includesSeconds(value) ? DateTimeWithSecondsFormat : DateTimeFormat;
  // A value carrying an explicit offset (e.g. a raw timestamp from a record link) is an absolute
  // instant, so render it as wall-clock time in the account timezone. A value without an offset is
  // already wall-clock time in the variable's own timezone, so keep its digits as-is — re-parsing
  // would otherwise reinterpret it in the browser timezone and shift it on every pass.
  return isoOffsetRegex.test(value) && timezone !== undefined
    ? formatInTimeZone(parsedValue, timezone, dateFormat)
    : format(parsedValue, dateFormat);
};

export const formatLocalDate = (date: Date): string => formatDate(date, DateFormat);

export const isInterval = (value: unknown): value is Interval =>
  isObject(value) &&
  Object.keys(value).every((key) =>
    ['years', 'months', 'days', 'hours', 'minutes', 'seconds', 'milliseconds'].includes(key),
  );

const IntervalConversions = [
  { from: 'milliseconds', to: 'seconds', factor: 1000 },
  { from: 'seconds', to: 'minutes', factor: 60 },
  { from: 'minutes', to: 'hours', factor: 60 },
  { from: 'hours', to: 'days', factor: 24 },
  { from: 'days', to: 'months', factor: 30 },
  { from: 'months', to: 'years', factor: 12 },
];

export const justifyInterval = (interval: Interval) => {
  const justifiedInterval = IntervalConversions.reduce(
    (memo, { from, to, factor }) => {
      const fromValue = memo[from as keyof Interval];
      const toValue = memo[to as keyof Interval] ?? 0;

      if (fromValue === undefined) {
        return memo;
      }

      if (Math.abs(fromValue) >= factor) {
        const quotient = Math.floor(Math.abs(fromValue) / factor);
        const remainder = Math.abs(fromValue) % factor;
        const sign = fromValue < 0 ? -1 : 1;

        return { ...memo, ...{ [to]: toValue + quotient * sign, [from]: remainder * sign } };
      }

      return memo;
    },
    { ...interval },
  );

  return omitBy(justifiedInterval, (value) => value === undefined || value === 0);
};

/**
 * Call a date-fns function in timezone.
 * @param date The date to convert.
 * @param tz The timezone to convert in.
 * @param fn The date-fns function to call.
 * @returns
 */
const calculateZonedDate = <
  T extends (date: Date | number | string, options?: StartOfWeekOptions) => Date,
>(
  date: Date | number | string,
  tz: string,
  fn: T,
  options?: StartOfWeekOptions,
): Date => fromZonedTime(fn(toZonedTime(date, tz), options), tz);

export function getDateRangeFromPrecision(
  date: Date,
  precision: TimeAggregationPeriod,
  timezone: string,
  options: StartOfWeekOptions = { weekStartsOn: 1 },
) {
  switch (precision) {
    case 'hour':
      return {
        startDate: calculateZonedDate(date, timezone, startOfHour),
        endDate: calculateZonedDate(date, timezone, endOfHour),
      };
    case 'day':
      return {
        startDate: calculateZonedDate(date, timezone, startOfDay),
        endDate: calculateZonedDate(date, timezone, endOfDay),
      };
    case 'week':
      return {
        startDate: calculateZonedDate(date, timezone, startOfWeek, options),
        endDate: calculateZonedDate(date, timezone, endOfWeek, options),
      };
    case 'month':
      return {
        startDate: calculateZonedDate(date, timezone, startOfMonth),
        endDate: calculateZonedDate(date, timezone, endOfMonth),
      };
    case 'quarter':
      return {
        startDate: calculateZonedDate(date, timezone, startOfQuarter),
        endDate: calculateZonedDate(date, timezone, endOfQuarter),
      };
    case 'year':
      return {
        startDate: calculateZonedDate(date, timezone, startOfYear),
        endDate: calculateZonedDate(date, timezone, endOfYear),
      };
    default:
      throw new Error(`Unsupported precision: ${precision}`);
  }
}
